import React from 'react';
import { ChevronDown, PlusCircle, Search, Users } from 'lucide-react';
import { FaLock } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import {
  COMMUNITY_TYPE_FILTER_OPTIONS,
  COMMUNITY_PILLAR_FILTER_OPTIONS,
} from '../utils/communityConstants';

/**
 * Locked preview of the portal Community page (/portal/community) for /start
 * prospects. Mirrors the portal layout — centered title with New Post, the
 * search + type/pillar filter row, the post feed, and the members FAB — with
 * every control inert. Filter options come from the shared community constants
 * so the preview shows the real post types and pillars.
 */

const LockedFilterSelect = ({ label, options }) => {
  const { darkMode } = useTheme();

  return (
    <div className="relative w-32 shrink-0">
      <select
        disabled
        aria-label={label}
        className={`w-full rounded-lg border py-2 pl-3 pr-9 text-sm appearance-none cursor-not-allowed ${
          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
        }`}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronDown
        className={`pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 ${
          darkMode ? 'text-gray-400' : 'text-gray-500'
        }`}
        aria-hidden
      />
    </div>
  );
};

const StartCommunity = () => {
  const { darkMode } = useTheme();
  const { isMobile } = useResponsive();

  return (
    <div className={`min-h-screen p-6 md:p-8 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
      <div className="max-w-7xl mx-auto">
        <div className={`relative mb-6 ${isMobile ? 'pt-10' : ''}`}>
          {!isMobile && (
            <span
              className="absolute top-0 right-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-red-600/60 text-white shadow-sm cursor-not-allowed"
              title="New Post — available to members"
            >
              <PlusCircle className="w-4 h-4 shrink-0" aria-hidden />
              New Post
              <FaLock className="text-[10px] shrink-0" aria-hidden />
            </span>
          )}

          <div className="text-center">
            <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Community
            </h1>
          </div>
        </div>

        <div className="mb-6 flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search
              className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
              aria-hidden
            />
            <input
              type="text"
              disabled
              placeholder="Search posts..."
              aria-label="Search posts"
              className={`w-full rounded-lg border py-2 pl-9 pr-3 text-sm cursor-not-allowed ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
            />
          </div>

          <LockedFilterSelect label="Type" options={COMMUNITY_TYPE_FILTER_OPTIONS} />
          <LockedFilterSelect label="Pillar" options={COMMUNITY_PILLAR_FILTER_OPTIONS} />
        </div>

        <div className="rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <FaLock
              className={`text-5xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}
              aria-hidden
            />
            <h2 className={`text-lg font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Community posts are locked
            </h2>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Join to see what other builders are sharing, ask questions, and post your own wins.
            </p>
          </div>
        </div>
      </div>

      <div className="fixed bottom-6 right-6 z-40">
        <span
          title="Members — available to members"
          className="w-12 h-12 rounded-full shadow-lg flex items-center justify-center bg-red-600/60 text-white cursor-not-allowed"
        >
          <Users className="w-5 h-5" aria-hidden />
        </span>
      </div>
    </div>
  );
};

export default StartCommunity;
